import { useAuth } from '@/lib/auth'
import { Button } from '@/components/ui'
import { ArhLogo } from '@/components/codex/ArhBrand'
import { LogIn, ShieldX, Loader2 } from 'lucide-react'
import { motion } from 'motion/react'
import type { ReactNode } from 'react'

export function AuthGate({ children }: { children: ReactNode }) {
  const { state, signIn, signOutNow, user } = useAuth()

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center constellation">
        <div className="flex items-center gap-3 text-cream/60">
          <Loader2 className="animate-spin" size={20} />
          <span className="font-mono text-xs uppercase tracking-[0.2em]">Authenticating</span>
        </div>
      </div>
    )
  }

  if (state === 'authorized') return <>{children}</>

  return (
    <div className="min-h-screen flex items-center justify-center px-6 constellation relative">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.2, 0.65, 0.3, 0.95] }}
        className="max-w-md w-full"
      >
        <div className="text-center mb-12">
          <ArhLogo framed className="w-80 max-w-full mx-auto mb-7 px-5 py-3" />
          <div className="eyebrow mb-3">Operator Access</div>
          <h1 className="font-display italic text-5xl text-cream font-light leading-none">
            Acquisition
            <br />
            <span className="text-gold-soft">Preparation</span>
          </h1>
          <div className="divider-editorial my-8 w-32 mx-auto" />
          <p className="font-body text-cream/55 text-sm leading-relaxed">
            Single-tenant operator console. Portfolio valuation,
            <br />
            exit-readiness, marketing decks, hosting inventory.
          </p>
        </div>

        <div className="glass rounded-xl p-8 backdrop-blur-xl">
          {state === 'unauthorized' ? (
            <>
              <div className="flex items-start gap-3 mb-6">
                <ShieldX size={20} className="text-danger shrink-0 mt-0.5" />
                <div>
                  <div className="font-heading text-lg text-cream mb-1">Access denied</div>
                  <p className="font-body text-sm text-cream/65">
                    Signed in as <span className="text-gold-soft">{user?.email ?? 'unknown'}</span>.
                    This account is not on the operator allowlist.
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-cream/40 mt-3">
                    Use an approved ARH operator account.
                  </p>
                </div>
              </div>
              <Button variant="ghost" className="w-full justify-center" onClick={signOutNow}>
                Sign out & try a different account
              </Button>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="eyebrow mb-2">Operator sign-in</div>
                <p className="font-body text-cream/65 text-sm">
                  This console is locked to authorized accounts only.
                </p>
              </div>
              <Button variant="primary" className="w-full justify-center" size="lg" onClick={signIn}>
                <LogIn size={18} />
                Continue with Google
              </Button>
              <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-cream/40 mt-4 text-center">
                Authorized accounts only
              </p>
            </>
          )}
        </div>

        <div className="text-center mt-8">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cream/30">
            ARH · Texas Series LLC · Doc #1576262380002
          </span>
        </div>
      </motion.div>
    </div>
  )
}
